import { KOKORO_VOICES } from './voiceMapping';
import type { VoiceBundleState } from './types';

export const STYLE_DIM = 256;
// Kokoro voice .bin files from HF are 510 rows of 256 float32 values
// (kokoro-js clamps its row index at 509 — maximum addressable row).
export const ROWS_PER_VOICE = 510;
export const BYTES_PER_VOICE = ROWS_PER_VOICE * STYLE_DIM * 4; // 522,240

const HF_BASE =
  'https://huggingface.co/onnx-community/Kokoro-82M-v1.0-ONNX/resolve/main/voices';
const CACHE_NAME = 'kokoro-voices';

type ProgressListener = (loaded: number, total: number) => void;
type StateListener = (state: VoiceBundleState) => void;

class VoiceBundleImpl {
  private state: VoiceBundleState = 'idle';
  /** rows[voiceIdx][rowIdx] → subarray view of length 256. */
  private rows: Float32Array[][] | null = null;
  private loadPromise: Promise<void> | null = null;
  private progressListeners = new Set<ProgressListener>();
  private stateListeners = new Set<StateListener>();
  private lastProgress = 0;

  getState(): VoiceBundleState {
    return this.state;
  }
  getProgress(): number {
    return this.lastProgress;
  }
  getRows(): Float32Array[][] | null {
    return this.rows;
  }

  onProgress(cb: ProgressListener): () => void {
    this.progressListeners.add(cb);
    return () => {
      this.progressListeners.delete(cb);
    };
  }
  onState(cb: StateListener): () => void {
    this.stateListeners.add(cb);
    return () => {
      this.stateListeners.delete(cb);
    };
  }

  private setState(s: VoiceBundleState): void {
    this.state = s;
    for (const l of this.stateListeners) l(s);
  }
  private emitProgress(loaded: number): void {
    this.lastProgress = loaded;
    for (const l of this.progressListeners) l(loaded, KOKORO_VOICES.length);
  }

  async ensureReady(): Promise<void> {
    if (this.state === 'ready' && this.rows) return;
    if (this.loadPromise) return this.loadPromise;
    this.setState('loading');
    this.lastProgress = 0;
    let loaded = 0;
    this.loadPromise = (async () => {
      try {
        const cache =
          typeof caches !== 'undefined' ? await caches.open(CACHE_NAME) : null;
        const voiceTables = await Promise.all(
          KOKORO_VOICES.map(async (name) => {
            const url = `${HF_BASE}/${name}.bin`;
            let buf: ArrayBuffer | null = null;
            if (cache) {
              const hit = await cache.match(url);
              if (hit) buf = await hit.arrayBuffer();
            }
            if (!buf) {
              const res = await fetch(url);
              if (!res.ok) {
                throw new Error(`voice ${name} → HTTP ${res.status}`);
              }
              buf = await res.arrayBuffer();
              if (cache) {
                try {
                  await cache.put(url, new Response(buf.slice(0)));
                } catch {
                  /* cache write is best-effort */
                }
              }
            }
            if (buf.byteLength !== BYTES_PER_VOICE) {
              throw new Error(
                `voice ${name} expected ${BYTES_PER_VOICE} bytes, got ${buf.byteLength}`,
              );
            }
            const flat = new Float32Array(buf);
            const rows: Float32Array[] = new Array(ROWS_PER_VOICE);
            for (let r = 0; r < ROWS_PER_VOICE; r++) {
              rows[r] = flat.subarray(r * STYLE_DIM, (r + 1) * STYLE_DIM);
            }
            loaded++;
            this.emitProgress(loaded);
            return rows;
          }),
        );
        this.rows = voiceTables;
        this.setState('ready');
      } catch (err) {
        this.setState('error');
        throw err;
      } finally {
        this.loadPromise = null;
      }
    })();
    return this.loadPromise;
  }

  async clearCache(): Promise<void> {
    if (typeof caches !== 'undefined') {
      try {
        await caches.delete(CACHE_NAME);
      } catch {
        /* ignore */
      }
    }
    this.rows = null;
    this.lastProgress = 0;
    this.setState('idle');
  }

  /** Test-only: preload the voice table without fetching from HuggingFace. */
  _setRowsForTest(rows: Float32Array[][]): void {
    this.rows = rows;
    this.state = 'ready';
    this.lastProgress = rows.length;
  }
}

export const voiceBundle = new VoiceBundleImpl();
